import { useContext, useState } from 'react';
import { useDialog } from '../../../../../common/dialog/useDialog';
import { ToastNotificationContext } from '../../../../../common/toast-notification';
import { BusinessError } from '../../../../../common/business-error';
import { VoiceAppEditData, VoiceAppFormData } from './interfaces';
import { addParams, fetchWithTokenRefresh } from '../../../../../../api/fetchWithTokenRefresh';
import endpoints from '../../../../../../api/endpoints';
import { handleResponseError } from '../../../../../../api/handleResponseError';
import { getEditAppBusinessError } from './businessErrors';

export type DialogError = BusinessError | null;
export const useEditApp = () => {
  const [initEditFormData, setInitEditFormData] = useState<VoiceAppFormData>();
  const [isEditAppLoading, setEditAppLoading] = useState<boolean>(false);
  const {
    isDialogOpen: isMainDialogOpen,
    handleOpenDialog: handleOpenMainDialog,
    handleCloseDialog: handleCloseMainDialog
  } = useDialog();

  const [error, setError] = useState<DialogError>(null);
  const { openToast } = useContext(ToastNotificationContext);

  const handleOpenEditAppDialog = (initVoiceAppSettings: VoiceAppFormData) => {
    setError(null);
    setInitEditFormData(initVoiceAppSettings);
    handleOpenMainDialog();
  };

  const handleCloseEditAppDialog = () => {
    setError(null);
    handleCloseMainDialog();
  };

  const handleSubmitEditAppDialog = async (voiceAppEditFormData: VoiceAppEditData, onSuccess: () => void) => {
    if (voiceAppEditFormData) {
      setError(null);
      setEditAppLoading(true);

      try {
        const response = await fetchWithTokenRefresh(
          endpoints.editVoiceAppSettings(voiceAppEditFormData.appId),
          addParams('PUT', voiceAppEditFormData)
        );

        if (response.status !== 204) {
          await handleResponseError(response);
        }

        onSuccess();
        setEditAppLoading(false);
        handleCloseEditAppDialog();
        openToast({
          message: `${voiceAppEditFormData.appId} app edited successfully`,
          severity: 'success'
        });
      } catch (e) {
        const { message, severity } = getEditAppBusinessError(e);

        setError({ message, severity });
        setEditAppLoading(false);
      }
    }
  };

  return {
    error,
    initEditFormData,
    isEditAppDialogOpen: isMainDialogOpen,
    isEditAppLoading,
    handleOpenEditAppDialog,
    handleCloseEditAppDialog,
    handleSubmitEditAppDialog
  };
};
